import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { BoardTheme } from './boardTheme';

/** The board re-polls every 5s and each poll carries a freshly-signed
 *  media URL — same file, new signature query string. Swapping a
 *  <video>'s src reloads it, so the clip would restart every 5s and
 *  never finish. Pin the URL by its path (the object key, stable per
 *  file) and only swap when the underlying file actually changes. */
function useStableMediaUrl(url: string | null): string | null {
  const ref = useRef<string | null>(null);
  if (url === null) {
    ref.current = null;
  } else if (ref.current === null || pathOf(ref.current) !== pathOf(url)) {
    ref.current = url;
  }
  return ref.current;
}

function pathOf(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url;
  }
}

/**
 * The media panel on the group board. Renders the uploaded image or video
 * (kind resolved server-side from the file's content type) or an empty
 * placeholder when no branding media is configured.
 */
export function BrandingPanel({
  mediaKind,
  mediaUrl,
  theme,
}: {
  mediaKind: 'image' | 'video' | null;
  mediaUrl: string | null;
  theme: BoardTheme;
}): JSX.Element {
  const { t } = useTranslation('qms');
  // Only the video URL is pinned: swapping a <video>'s src restarts the
  // clip, so it must survive the 5s poll. A still image has nothing to
  // restart — it keeps the freshly-signed URL from each poll. Pinning an
  // image instead would freeze its first signed URL until that signature
  // expires (FILE_URL_TTL_SECONDS), after which storage rejects it and the
  // image fails to load with a permission error.
  const stableVideoUrl = useStableMediaUrl(
    mediaKind === 'video' ? mediaUrl : null,
  );

  if (mediaKind === 'image' && mediaUrl) {
    return (
      <aside
        className="relative h-full w-full overflow-hidden rounded-3xl border"
        style={{ borderColor: theme.border, backgroundColor: theme.surface }}
        data-testid="display-branding"
        data-mode="image"
      >
        <img
          src={mediaUrl}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover"
        />
      </aside>
    );
  }

  if (mediaKind === 'video' && stableVideoUrl) {
    return (
      <aside
        className="relative h-full w-full overflow-hidden rounded-3xl border"
        style={{ borderColor: theme.border, backgroundColor: theme.surface }}
        data-testid="display-branding"
        data-mode="video"
      >
        <video
          src={stableVideoUrl}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          className="h-full w-full object-cover"
        />
      </aside>
    );
  }

  return (
    <aside
      className="flex h-full w-full items-center justify-center rounded-3xl border p-12 text-center"
      style={{ borderColor: theme.border, backgroundColor: theme.surface }}
      data-testid="display-branding"
      data-mode="empty"
    >
      <p
        className="max-w-3xl text-2xl font-medium"
        style={{ color: theme.muted }}
      >
        {t('displays.public.branding_empty')}
      </p>
    </aside>
  );
}
